import React, { useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Brain,
  Briefcase,
  ClipboardCheck,
  Database,
  FileText,
  Landmark,
  ListChecks,
  MessagesSquare,
  Phone,
  Scale,
  ShieldCheck,
  Target,
  Umbrella,
  UserRound,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  CapabilityGrid,
  CONTACT_PATH,
  DEMO_PATH,
  EvidenceBoard,
  IndustryCta,
  IndustryFlowStyles,
  ProblemGrid,
  Section,
  SectionIntro,
} from './industryUi';

const PROBLEMS = [
  {
    title: 'Domain fit',
    detail: 'A resume can list experience without showing the banking, insurance, or financial-services exposure this role needs.',
    icon: BookOpen,
  },
  {
    title: 'Communication',
    detail: 'Customer-facing and stakeholder roles depend on clear, professional communication.',
    icon: MessagesSquare,
  },
  {
    title: 'Role-specific capability',
    detail: 'Risk, compliance, credit, finance, and technology roles need different evaluation criteria.',
    icon: Target,
  },
  {
    title: 'Consistent evaluation',
    detail: 'Manual screening can change from recruiter to recruiter, even for the same role.',
    icon: Scale,
  },
];

const SIGNALS = [
  {
    title: 'Experience',
    detail: 'What the resume shows about prior work.',
    icon: Briefcase,
  },
  {
    title: 'Domain knowledge',
    detail: 'Exposure relevant to banking, insurance, or financial services.',
    icon: BookOpen,
  },
  {
    title: 'Communication',
    detail: 'How clearly the candidate explains the role.',
    icon: MessagesSquare,
  },
  {
    title: 'Behaviour',
    detail: 'How they respond in workplace scenarios.',
    icon: Brain,
  },
  {
    title: 'Technical competency',
    detail: 'The skills this job actually asks for.',
    icon: ListChecks,
  },
  {
    title: 'Role-specific assessments',
    detail: 'Checks chosen for this job, not one template for every hire.',
    icon: ClipboardCheck,
  },
];

const WORKFLOW = [
  {
    title: 'Resume Intelligence',
    detail: 'Read the resume against the job description before a recruiter spends time on it.',
    icon: FileText,
  },
  {
    title: 'Domain Screening',
    detail: 'Look for background that fits banking, insurance, or financial services for this role.',
    icon: BookOpen,
  },
  {
    title: 'Role-specific Assessment',
    detail: 'Use the assessment that matches the job, rather than one generic test for every role.',
    icon: ClipboardCheck,
  },
  {
    title: 'AI / Structured Interview',
    detail: 'Capture a structured interview the team can review.',
    icon: Video,
  },
  {
    title: 'Candidate Evidence',
    detail: 'The recruiter reviews the resume, assessment, and interview records.',
    icon: ListChecks,
  },
  {
    title: 'Recruiter Decision',
    detail: 'A person decides who moves forward.',
    icon: UserRound,
  },
];

const ROLE_GROUPS = [
  {
    title: 'Banking & finance',
    roles: ['Banking', 'Investment Banking', 'Wealth Management', 'Finance', 'Credit'],
    icon: Landmark,
  },
  {
    title: 'Risk & control',
    roles: ['Risk', 'Compliance'],
    icon: ShieldCheck,
  },
  {
    title: 'Insurance',
    roles: ['Insurance'],
    icon: Umbrella,
  },
  {
    title: 'Tech & data',
    roles: ['FinTech', 'Financial Data', 'Technology'],
    icon: Database,
  },
];

const ROLE_EXAMPLES = [
  {
    role: 'Risk Analyst',
    criteria: ['Domain knowledge', 'Analytical competency', 'Communication'],
  },
  {
    role: 'Relationship Manager',
    criteria: ['Experience', 'Communication', 'Behaviour'],
  },
  {
    role: 'Credit role',
    criteria: ['Domain knowledge', 'Role assessment', 'Interview'],
  },
  {
    role: 'Technology role',
    criteria: ['Technical assessment', 'AI interview', 'Behaviour'],
  },
];

const CAPABILITIES = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Whether the background on the resume fits this role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: FileText,
  },
  {
    title: 'AI Call Screening',
    helps: 'How the candidate answers role questions on a structured call.',
    href: '/hirewithus/ai-call-screening',
    icon: Phone,
  },
  {
    title: 'Screening Assessment',
    helps: 'How the resume scores against the job description.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Behaviour, communication, and role-based skills.',
    href: '/hirewithus/functional-assessment',
    icon: Brain,
  },
  {
    title: 'Virtual Hiring',
    helps: 'A role-based AI interview the team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

const EVIDENCE = ['Resume', 'Domain', 'Assessment', 'Communication', 'Behaviour', 'Interview'];

const HERO_STEPS = [
  { label: 'Candidate profile', note: 'Role and background' },
  { label: 'Domain screening', note: 'Industry exposure for this job' },
  { label: 'Role assessment', note: 'Criteria chosen for the role' },
  { label: 'AI interview', note: 'Structured questions' },
  { label: 'Evidence profile', note: 'Ready for a recruiter' },
];

export default function BfsiHiring() {
  useSEO({
    title: 'AI Recruitment for BFSI | SearchJob.ai',
    description:
      'AI-powered hiring for banking, financial services and insurance with screening, assessments and interviews.',
    canonical: 'https://searchjob.ai/industries/bfsi',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <Problem />
        </ScrollReveal>
        <ScrollReveal>
          <Framework />
        </ScrollReveal>
        <ScrollReveal>
          <Workflow />
        </ScrollReveal>
        <ScrollReveal>
          <RoleSpecific />
        </ScrollReveal>
        <ScrollReveal>
          <ReviewReady />
        </ScrollReveal>
        <ScrollReveal>
          <Capabilities />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 pb-12 pt-7 sm:px-6 lg:grid-cols-[minmax(0,1.04fr)_minmax(24rem,0.96fr)] lg:gap-14 lg:px-8 lg:pb-14 lg:pt-8">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Banking, Financial Services & Insurance
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Hire for Capability. <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Screen for Risk.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            AI-powered recruitment for banking, financial services and insurance.
          </p>
          <ul className="mx-auto mb-8 flex max-w-2xl flex-wrap justify-center gap-x-5 gap-y-2 text-sm font-medium text-foreground lg:mx-0 lg:justify-start">
            {['Role-specific criteria', 'Multiple evidence points', 'Recruiter-led decisions'].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Build Your BFSI Hiring Pipeline
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href={CONTACT_PATH}>Talk to a BFSI Hiring Expert</a>
            </Button>
          </div>
        </div>
        <HeroReview />
      </div>
    </section>
  );
}

function HeroReview() {
  return (
    <aside className="relative self-start" aria-label="Example BFSI candidate review workflow">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <div className="overflow-hidden rounded-3xl border border-primary bg-primary/5 shadow-[0_18px_50px_hsl(164_33%_7%_/_0.08)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primary/20 bg-primary/5 px-4 py-3 sm:px-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Example hiring review</p>
            <p className="mt-1 text-sm font-medium text-foreground">Evidence before a recruiter decides</p>
          </div>
          <p className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium text-muted-foreground">Illustrative</p>
        </div>
        <ol className="p-4 sm:p-5">
          {HERO_STEPS.map((step, index) => (
            <li key={step.label} className="relative flex gap-3 pb-2 last:pb-0">
              {index < HERO_STEPS.length - 1 ? (
                <span className="industry-flow-line absolute left-[13px] top-7 h-[calc(100%-1.1rem)] w-px" aria-hidden="true" />
              ) : null}
              <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/25 bg-background text-xs font-semibold text-primary">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 rounded-xl border border-primary/20 bg-background px-3 py-2">
                <span className="block text-sm font-semibold text-foreground">{step.label}</span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{step.note}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </aside>
  );
}

function Problem() {
  return (
    <Section muted compact>
      <SectionIntro
        eyebrow="Why BFSI hiring is different"
        title="BFSI hiring needs more than resume matching."
        copy="The same CV can look qualified and still leave out domain fit, communication, and the criteria this role actually uses."
      />
      <ProblemGrid items={PROBLEMS} />
    </Section>
  );
}

function Framework() {
  const [active, setActive] = useState(0);
  const current = SIGNALS[active];
  const left = SIGNALS.slice(0, 3);
  const right = SIGNALS.slice(3);

  return (
    <Section compact>
      <SectionIntro
        eyebrow="Screening framework"
        title="Six signals around one candidate."
        copy="Select a signal. Each one answers a different hiring question."
      />
      <div className="grid items-center gap-4 lg:grid-cols-[1fr_17rem_1fr]">
        <div className="order-2 grid gap-3 sm:grid-cols-2 lg:order-none lg:grid-cols-1">
          {left.map((signal) => (
            <SignalButton
              key={signal.title}
              signal={signal}
              selected={signal.title === current.title}
              onSelect={() => setActive(SIGNALS.indexOf(signal))}
            />
          ))}
        </div>
        <article className="order-1 rounded-3xl border border-primary bg-primary/5 p-5 text-center lg:order-none" aria-live="polite">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Example candidate</p>
          <p className="mt-3 text-lg font-semibold text-foreground">{current.title}</p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{current.detail}</p>
          <p className="mt-4 text-sm font-semibold text-foreground">A person still decides.</p>
        </article>
        <div className="order-3 grid gap-3 sm:grid-cols-2 lg:order-none lg:grid-cols-1">
          {right.map((signal) => (
            <SignalButton
              key={signal.title}
              signal={signal}
              selected={signal.title === current.title}
              onSelect={() => setActive(SIGNALS.indexOf(signal))}
            />
          ))}
        </div>
      </div>
    </Section>
  );
}

function SignalButton({ signal, selected, onSelect }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`group flex min-h-11 w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none ${
        selected ? 'border-primary bg-primary/5' : 'border-primary/30 bg-primary/5 hover:border-primary/45'
      }`}
    >
      <span
        className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors duration-200 ${
          selected
            ? 'border-primary/30 bg-primary/10 text-primary'
            : 'border-primary/20 bg-primary/10 text-primary group-hover:border-primary/30'
        }`}
      >
        <signal.icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{signal.title}</span>
        <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">{signal.detail}</span>
      </span>
    </button>
  );
}

function Workflow() {
  return (
    <Section id="bfsi-workflow" muted compact>
      <SectionIntro
        eyebrow="BFSI hiring workflow"
        title="One review path, tailored to the role."
        copy="Each stage adds context to the candidate record before a recruiter decides what happens next."
      />
      <ol className="grid gap-3 rounded-3xl border border-primary/30 bg-primary/5 p-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="BFSI hiring workflow">
        {WORKFLOW.map((step, index) => {
          const Icon = step.icon;
          return (
            <li key={step.title} className="flex min-h-44 flex-col rounded-2xl border border-primary/30 bg-primary/5 px-5 py-5 sm:px-6">
              <div className="flex items-center justify-between gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-white/70 text-primary">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-xs font-semibold tabular-nums text-primary/70">{String(index + 1).padStart(2, '0')}</span>
              </div>
              <h3 className="mt-5 text-base font-semibold text-foreground">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.detail}</p>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function RoleSpecific() {
  return (
    <Section compact>
      <SectionIntro
        eyebrow="Role-specific evaluation"
        title="The criteria should change with the role."
        copy="BFSI hiring is not one template. The workflow adapts to the expertise, risk context, and communication needs of each role."
      />
      <div className="grid gap-8 lg:grid-cols-2 lg:items-stretch">
        <section className="flex min-w-0 flex-col" aria-labelledby="bfsi-role-areas">
          <p id="bfsi-role-areas" className="text-xs font-semibold uppercase tracking-wider text-primary">Common BFSI role areas</p>
          <ul className="mt-3 grid flex-1 auto-rows-fr gap-2 sm:grid-cols-2">
            {ROLE_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <li key={group.title} className="h-full">
                  <article className="group flex h-full min-h-24 items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-3 py-3 transition duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-sm motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none sm:px-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold leading-snug text-foreground">{group.title}</h3>
                      <ul className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs leading-5 text-muted-foreground">
                        {group.roles.map((role) => (
                          <li key={role} className="inline-flex items-center gap-1">
                            <span className="h-1 w-1 rounded-full bg-primary/60" aria-hidden="true" />
                            {role}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        </section>
        <section className="flex min-w-0 flex-col" aria-labelledby="bfsi-evaluation-plans">
          <p id="bfsi-evaluation-plans" className="text-xs font-semibold uppercase tracking-wider text-primary">Example evaluation plans</p>
          <div className="mt-3 flex-1 rounded-2xl border border-primary/30 bg-primary/5 px-3 py-3 sm:px-4">
            <ul className="flex h-full flex-col justify-between gap-2">
              {ROLE_EXAMPLES.map((example) => (
                <li key={example.role} className="grid gap-2 py-2.5 sm:grid-cols-[minmax(8.5rem,0.8fr)_minmax(0,1.2fr)] sm:items-center sm:gap-3">
                  <h3 className="text-sm font-semibold text-foreground">{example.role}</h3>
                  <ul className="flex flex-wrap gap-x-3 gap-y-1.5 lg:flex-nowrap lg:justify-end">
                    {example.criteria.map((item) => (
                      <li key={item} className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground">
                        <span className="h-1 w-1 rounded-full bg-primary/60" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </Section>
  );
}

function ReviewReady() {
  return (
    <Section muted compact>
      <SectionIntro
        eyebrow="Candidate evidence"
        title="Give the recruiter one review-ready record."
        copy="Resume, assessment, communication, behaviour, and interview evidence stay together. The example below is illustrative, not a live candidate."
      />
      <EvidenceBoard
        profileMark="RM"
        profileTitle="Relationship role"
        profileNote="Sample role"
        signals={EVIDENCE}
        reviewLabel="BFSI evidence for review"
        nextLabel="Next: recruiter review"
        signalMotion
        singleCard
      />
    </Section>
  );
}

function Capabilities() {
  return (
    <Section compact>
      <SectionIntro
        eyebrow="Product capabilities"
        title="What each product helps a recruiter evaluate."
        copy="These products already exist on SearchJob.ai. Nothing here is a BFSI-only feature."
      />
      <CapabilityGrid items={CAPABILITIES} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="Build Your BFSI Hiring Pipeline"
      titleLead="Build Your BFSI"
      titleAccent="Hiring Pipeline"
      copy="Create a role-specific screening, assessment and interview workflow for your BFSI team."
      primaryLabel="Build Your BFSI Hiring Pipeline"
      primaryHref={DEMO_PATH}
      secondaryLabel="Talk to a BFSI Hiring Expert"
      secondaryHref={CONTACT_PATH}
      compact
      variant="emerald"
    />
  );
}
